import { ActionIcon, Avatar, Box, Divider, Flex, Group, Menu, Paper, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconBell, IconCoins, IconHelpCircle, IconLogout, IconPencil, IconSettings } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";
import DashboardCard from "./DashboardCard";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { userDetails, pendingRequestCounts, collaborationSidebarCounts, shortlistedIds, logout } = useUserContext();
  const userName = userDetails?.name?.trim() || "User";
  const companyName = userDetails?.company?.legalName || "Company Name";
  const gtid = userDetails?.company?.gtid || "User";

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      <Box py="md" style={{ borderBottom: "1px solid #E5E7EB" }}>
        <Flex
          justify="space-between"
          pl={20}
          pr={20}
          align="flex-start"
          direction={{ base: "column", sm: "row" }}
          gap="sm"
        >
          <Box>
            <Title order={2} fw={400} fz={24}>
              Welcome! {userName}
            </Title>
            <Text c="dimmed" mt={6}>
              Here is your dashboard overview.
            </Text>
          </Box>

          <Group gap="sm" wrap="nowrap" align="center">
            <Divider orientation="vertical" h={28} />
            <ActionIcon size="lg" variant="light" radius="xl" aria-label="Notifications">
              <IconBell size={18} />
            </ActionIcon>
            <Menu shadow="md" width={230} position="bottom-end">
              <Menu.Target>
                <Avatar
                  radius="xl"
                  size="md"
                  color="blue"
                  style={{ cursor: "pointer" }}
                >
                  {userName.charAt(0).toUpperCase()}
                </Avatar>
              </Menu.Target>
              <Menu.Dropdown>
                <Box px="sm" py={6}>
                  <Group gap={8} wrap="nowrap">
                    <Avatar radius="xl" size="sm" style={{ backgroundColor: "#eef2ff", color: "#1d4ed8" }}>
                      <IconSettings size={14} />
                    </Avatar>
                    <Box style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} truncate>
                        {companyName}
                      </Text>
                      <Text size="xs" c="dimmed" truncate>
                        {gtid}
                      </Text>
                    </Box>
                  </Group>
                </Box>
                <Menu.Divider />
                <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => navigate("/profile/edit")}>
                  Edit Profile
                </Menu.Item>
                <Menu.Item leftSection={<IconCoins size={16} />} onClick={() => navigate("/credits")}>
                  Credits
                </Menu.Item>
                <Menu.Item leftSection={<IconHelpCircle size={16} />} onClick={() => navigate("/settings")}>
                  Settings
                </Menu.Item>
                <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={logout}>
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Flex>
      </Box>

      <Box p="lg">
        <Stack gap="lg">
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md">
            <Box>
              <DashboardCard
                title="Shortlist"
                count={shortlistedIds.size}
                description="Companies you have shortlisted"
                onClick={() => navigate("/shortlist")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Sent"
                count={pendingRequestCounts.sent}
                description="Requests you've sent waiting for response"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Received"
                count={pendingRequestCounts.received}
                description="Requests you've received from others"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Active Collaborations"
                count={collaborationSidebarCounts.active}
                description="Ongoing collaborations with partners"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Closed Collaborations"
                count={collaborationSidebarCounts.closed}
                description="Completed or inactive collaborations"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
          </SimpleGrid>

          <Paper p="lg" radius="md" withBorder>
            <Text fw={600} mb="xs">
              Quick Notes
            </Text>
            <Text c="dimmed">
              These cards are powered by the same live counters used in the sidebar.
            </Text>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}
